import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { notifySidebarCount } from '../../lib/sidebar-counts';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Store, 
  Filter, 
  Plus, 
  Edit,
  Trash2,
  RefreshCw,
  X,
  Globe,
  CreditCard,
  ShoppingCart,
  Calendar,
  Search,
  Timer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Checkbox } from '../ui/checkbox';
import { formatCurrencySelector, getCurrencySymbol, isSpecificCurrency } from '../../utils/currencyFormatter';

// Helper to format date for API
const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const getMerchantGroupIcon = (group) => {
  const icons = {
    'ecommerce': '🛒',
    'retail': '🏪',
    'saas': '💻',
    'marketplace': '🏢',
    'subscription': '📋',
    'gaming': '🎮',
    'travel': '✈️',
    'food_delivery': '🍕',
    'financial_services': '💳',
    'healthcare': '🏥',
    'education': '📚',
    'non_profit': '🤝',
    'other': '📦'
  };
  return icons[group] || '📦';
};

const getMerchantGroupColor = (group) => {
  const colors = {
    'ecommerce': 'bg-blue-100 text-blue-800',
    'retail': 'bg-green-100 text-green-800',
    'saas': 'bg-purple-100 text-purple-800',
    'marketplace': 'bg-orange-100 text-orange-800',
    'subscription': 'bg-indigo-100 text-indigo-800',
    'gaming': 'bg-pink-100 text-pink-800',
    'travel': 'bg-cyan-100 text-cyan-800',
    'food_delivery': 'bg-red-100 text-red-800',
    'financial_services': 'bg-yellow-100 text-yellow-800',
    'healthcare': 'bg-emerald-100 text-emerald-800',
    'education': 'bg-violet-100 text-violet-800',
    'non_profit': 'bg-rose-100 text-rose-800',
    'other': 'bg-gray-100 text-gray-800'
  };
  return colors[group] || 'bg-gray-100 text-gray-800';
};

const getStatusColor = (status) => {
  const colors = {
    'active': 'bg-green-100 text-green-800',
    'inactive': 'bg-gray-100 text-gray-800',
    'suspended': 'bg-red-100 text-red-800',
    'pending': 'bg-yellow-100 text-yellow-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const Merchants = () => {
  const { token, selectedPlatform, user , backendUrl} = useAuth();
  const { currentPlatform } = usePlatform();
  const { theme } = useTheme();
  
  // Dynamic API URL for dev/prod environments
  const API = `${backendUrl}/api`;
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    merchant_group: '',
    status: '',
    country: '',
    currency: '',
    uses_virtual_cards: ''  // '', 'true', 'false'
  });

  // Default the currency filter to the platform's configured default currency.
  // Re-applies whenever the platform (or its default) changes; user can still
  // switch to "All Currencies" or any other afterwards. ('' is the all-currencies sentinel here)
  const platformDefaultCurrency = (selectedPlatform && selectedPlatform !== 'all')
    ? (currentPlatform?.default_currency || '')
    : '';
  useEffect(() => {
    if (!currentPlatform) return;
    setFilters(prev => ({ ...prev, currency: platformDefaultCurrency }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, platformDefaultCurrency]);

  // Money totals: when a specific currency is selected show that currency's
  // sign (and the column label shows its code); otherwise show USD-equivalent.
  const displayCurrency = isSpecificCurrency(filters.currency) ? filters.currency : 'USD';
  const currencySymbol = getCurrencySymbol(displayCurrency);
  const fetchSeqRef = useRef(0);
  const [availableFilters, setAvailableFilters] = useState({
    merchant_groups: [],
    countries: [],
    currencies: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 50,
    total: 0,
    total_pages: 0
  });
  
  // Aggregates for summary cards
  const [aggregates, setAggregates] = useState({
    total_sales: 0,
    total_transactions: 0
  });
  // Client-measured load time (ms) of the most recent data fetch
  const [loadTimeMs, setLoadTimeMs] = useState(null);
  
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [newMerchant, setNewMerchant] = useState({
    name: '',
    merchant_group: 'other',
    email: '',
    website: '',
    business_type: '',
    description: '',
    uses_virtual_cards: false,
    primary_country: 'US',
    primary_currency: 'USD',
    primary_processor: 'stripe'
  });
  
  // Period filter state (same as Transaction History)
  const [periodFilter, setPeriodFilter] = useState({
    period: 'thismonth',
    customMonth: new Date().getMonth() + 1,
    customYear: new Date().getFullYear()
  });

  // Helper function to get date range based on period filter
  const getDateRange = () => {
    const now = new Date();
    
    switch (periodFilter.period) {
      case 'today':
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(todayStart),
          end_date: formatLocalDateTime(todayEnd)
        };
      case 'last7days':
        const sevenDaysStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 0, 0, 0);
        const todayEndLast7 = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        return {
          start_date: formatLocalDateTime(sevenDaysStart),
          end_date: formatLocalDateTime(todayEndLast7)
        };
      case 'thismonth':
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisMonthStart),
          end_date: formatLocalDateTime(thisMonthEnd)
        };
      case 'thisyear':
        const thisYearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        const thisYearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(thisYearStart),
          end_date: formatLocalDateTime(thisYearEnd)
        };
      case 'custom':
        const customStart = new Date(periodFilter.customYear, periodFilter.customMonth - 1, 1, 0, 0, 0);
        const customEnd = new Date(periodFilter.customYear, periodFilter.customMonth, 0, 23, 59, 59);
        return {
          start_date: formatLocalDateTime(customStart),
          end_date: formatLocalDateTime(customEnd)
        };
      case 'alltime':
        return { start_date: null, end_date: null };
      default:
        return { start_date: null, end_date: null };
    }
  };

  useEffect(() => {
    if (token && selectedPlatform) {
      fetchMerchants();
      fetchAvailableFilters();
    }
  }, [token, selectedPlatform]);

  useEffect(() => {
    if (token && selectedPlatform) {
      fetchMerchants();
    }
  }, [pagination.page, filters, searchQuery, periodFilter, token, selectedPlatform]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ merchant_group: '', status: '', country: '', currency: '', uses_virtual_cards: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
    setPeriodFilter({
      period: 'thismonth',
      customMonth: new Date().getMonth() + 1,
      customYear: new Date().getFullYear()
    });
  };

  const handleRefresh = () => {
    fetchMerchants();
  };

  const fetchMerchants = async () => {
    if (!token) return;
    // Latest-request-wins guard: prevents a stale "All currencies" response from
    // overwriting the data after the currency filter switches to the platform default.
    const seq = ++fetchSeqRef.current;
    const t0 = performance.now();
    
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.per_page.toString(),
        platform_id: selectedPlatform
      });

      if (filters.merchant_group) {
        params.append('merchant_group', filters.merchant_group);
      }

      if (filters.status) {
        params.append('status', filters.status);
      }

      if (filters.country) {
        params.append('country', filters.country);
      }

      if (filters.currency) {
        params.append('currency', filters.currency);
      }

      if (filters.uses_virtual_cards) {
        params.append('uses_virtual_cards', filters.uses_virtual_cards);
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      // Add date range from period filter
      const dateRange = getDateRange();
      if (dateRange.start_date) {
        params.append('start_date', dateRange.start_date);
      }
      if (dateRange.end_date) {
        params.append('end_date', dateRange.end_date);
      }

      const response = await axios.get(`${backendUrl}/api/merchants?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (seq !== fetchSeqRef.current) return; // superseded by a newer request
      setMerchants(response.data.merchants);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        total_pages: response.data.total_pages
      }));
      // Note: sidebar totals are sourced from /api/platform-stats (all-time).
      // Removed merchants emit so filter changes here cannot overwrite the
      // platform-wide total in the sidebar.
      setAggregates({
        total_sales: response.data.total_sales || 0,
        total_transactions: response.data.total_transactions || 0
      });
      setLoadTimeMs(performance.now() - t0);
      setError(null);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching merchants:', err);
      setError('Failed to load merchants');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  const fetchAvailableFilters = async () => {
    if (!token) return;
    
    try {
      const [countriesRes, currenciesRes] = await Promise.all([
        axios.get(`${backendUrl}/api/filters/countries?platform_id=${selectedPlatform}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${backendUrl}/api/filters/currencies?platform_id=${selectedPlatform}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAvailableFilters({
        merchant_groups: [
          { value: 'ecommerce', label: 'E-commerce' },
          { value: 'retail', label: 'Retail' },
          { value: 'saas', label: 'SaaS' },
          { value: 'marketplace', label: 'Marketplace' },
          { value: 'subscription', label: 'Subscription' },
          { value: 'gaming', label: 'Gaming' },
          { value: 'travel', label: 'Travel' },
          { value: 'food_delivery', label: 'Food Delivery' },
          { value: 'financial_services', label: 'Financial Services' },
          { value: 'healthcare', label: 'Healthcare' },
          { value: 'education', label: 'Education' },
          { value: 'non_profit', label: 'Non-Profit' },
          { value: 'other', label: 'Other' }
        ],
        countries: countriesRes.data.countries,
        currencies: currenciesRes.data.currencies || []
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const createMerchant = async () => {
    if (!token) return;
    
    try {
      const merchantData = {
        ...newMerchant,
        countries: [newMerchant.primary_country],
        currencies: [newMerchant.primary_currency],
        processors: [newMerchant.primary_processor],
        platform_id: selectedPlatform === 'all' ? undefined : selectedPlatform
      };

      await axios.post(`${backendUrl}/api/merchants`, merchantData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowCreateDialog(false);
      setNewMerchant({
        name: '',
        merchant_group: 'other',
        email: '',
        website: '',
        business_type: '',
        description: '',
        uses_virtual_cards: false,
        primary_country: 'US',
        primary_currency: 'USD',
        primary_processor: 'stripe'
      });
      fetchMerchants();
    } catch (err) {
      console.error('Error creating merchant:', err);
      setError('Failed to create merchant');
    }
  };

  const openEditDialog = (merchant) => {
    setEditingMerchant({
      ...merchant,
      primary_country: merchant.primary_country || 'US',
      primary_currency: merchant.primary_currency || 'USD',
      primary_processor: merchant.primary_processor || 'stripe',
      uses_virtual_cards: merchant.uses_virtual_cards || false
    });
    setShowEditDialog(true);
  };

  const updateMerchant = async () => {
    if (!token || !editingMerchant) return;
    
    try {
      const updateData = {
        name: editingMerchant.name,
        merchant_group: editingMerchant.merchant_group,
        email: editingMerchant.email,
        website: editingMerchant.website,
        business_type: editingMerchant.business_type,
        description: editingMerchant.description,
        uses_virtual_cards: !!editingMerchant.uses_virtual_cards,
        countries: [editingMerchant.primary_country],
        primary_country: editingMerchant.primary_country,
        currencies: [editingMerchant.primary_currency],
        primary_currency: editingMerchant.primary_currency,
        processors: [editingMerchant.primary_processor],
        primary_processor: editingMerchant.primary_processor
      };

      await axios.patch(`${backendUrl}/api/merchants/${editingMerchant.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowEditDialog(false);
      setEditingMerchant(null);
      fetchMerchants();
    } catch (err) {
      console.error('Error updating merchant:', err);
      setError('Failed to update merchant');
    }
  };

  const deleteMerchant = async (merchantId, merchantName) => {
    if (!token || !window.confirm(`Are you sure you want to delete "${merchantName}"? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${backendUrl}/api/merchants/${merchantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchMerchants();
    } catch (err) {
      console.error('Error deleting merchant:', err);
      setError('Failed to delete merchant');
    }
  };

  if (loading && merchants.length === 0) {
    return (
      <div className="space-y-8 p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading merchants...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" style={{ color: 'var(--input-text-color)' }}>
            <Store className="w-8 h-8 text-purple-500" />
            Merchants
          </h1>
          <p className="text-muted-foreground mt-2">
            {selectedPlatform === 'all' ? (
              'Manage merchants across all platforms'
            ) : (
              'Manage merchants and their business information'
            )}
          </p>
        </div>
        
        {/* Summary Card */}
        <Card className="shrink-0">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Store className="h-3 w-3" />
              Period Summary
              {loadTimeMs != null && (
                <span
                  className={`ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${loadTimeMs < 1000 ? 'bg-green-50 text-green-600' : loadTimeMs < 3000 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}
                  title="Time to load this view"
                  data-testid="merchants-load-time"
                >
                  <Timer className="h-3 w-3" />
                  {(loadTimeMs / 1000).toFixed(2)}s
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3 px-4">
            <div className="flex gap-6">
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Merchants</span>
                <span className="text-lg font-semibold">{pagination.total.toLocaleString()}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Total Sales ({filters.currency || 'USD'})</span>
                <span className="text-lg font-semibold text-green-600">{currencySymbol}{aggregates.total_sales.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs text-muted-foreground">Transactions</span>
                <span className="text-lg font-semibold">{aggregates.total_transactions.toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-4 flex-wrap gap-4">
              {/* Period Filter - First Position */}
              <div className="flex items-center gap-2">
                <Select value={periodFilter.period} onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, period: value }))}>
                  <SelectTrigger className="w-36">
                    <Calendar className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="last7days">Last 7 Days</SelectItem>
                    <SelectItem value="thismonth">This Month</SelectItem>
                    <SelectItem value="thisyear">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                    <SelectItem value="alltime">All Time</SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Month/Year Selectors */}
                {periodFilter.period === 'custom' && (
                  <>
                    <Select 
                      value={periodFilter.customMonth.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customMonth: parseInt(value) }))}
                    >
                      <SelectTrigger className="w-28">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Select 
                      value={periodFilter.customYear.toString()} 
                      onValueChange={(value) => setPeriodFilter(prev => ({ ...prev, customYear: parseInt(value) }))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
              
              {/* Search Box */}
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search merchants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent w-64"
                />
              </div>
              
              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Status:</label>
                <Select value={filters.status || "all"} onValueChange={(value) => handleFilterChange('status', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Group Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Group:</label>
                <Select value={filters.merchant_group || "all"} onValueChange={(value) => handleFilterChange('merchant_group', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Groups" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Groups</SelectItem>
                    {availableFilters.merchant_groups.map((group) => (
                      <SelectItem key={group.value} value={group.value}>
                        {getMerchantGroupIcon(group.value)} {group.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Currency Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Currency:</label>
                <Select value={filters.currency || "all"} onValueChange={(value) => handleFilterChange('currency', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-32" data-testid="merchants-currency-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {Array.from(new Set([
                      ...(availableFilters.currencies || []),
                      ...((filters.currency && filters.currency !== 'all') ? [filters.currency] : []),
                    ])).map((currency) => (
                      <SelectItem key={currency} value={currency}>
                        {formatCurrencySelector(currency)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Virtual Cards:</label>
                <Select value={filters.uses_virtual_cards || "all"} onValueChange={(value) => handleFilterChange('uses_virtual_cards', value === "all" ? '' : value)}>
                  <SelectTrigger className="w-32" data-testid="merchant-filter-virtual-cards">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(filters.status || filters.merchant_group || filters.currency || filters.uses_virtual_cards || searchQuery) && (
                <Button variant="outline" size="sm" onClick={() => { clearFilters(); setSearchQuery(''); }}>
                  <X className="h-4 w-4 mr-2" />
                  Clear
                </Button>
              )}
              
              <Button onClick={handleRefresh} variant="outline" size="sm" disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>

      {/* Edit Merchant Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Merchant</DialogTitle>
          </DialogHeader>
          {editingMerchant && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Merchant Name</label>
                <Input
                  value={editingMerchant.name}
                  onChange={(e) => setEditingMerchant({...editingMerchant, name: e.target.value})}
                  placeholder="Enter merchant name"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Business Type</label>
                <Select value={editingMerchant.merchant_group} onValueChange={(value) => setEditingMerchant({...editingMerchant, merchant_group: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableFilters.merchant_groups.map((group) => (
                      <SelectItem key={group.value} value={group.value}>
                        {getMerchantGroupIcon(group.value)} {group.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Email</label>
                <Input
                  type="email"
                  value={editingMerchant.email || ''}
                  onChange={(e) => setEditingMerchant({...editingMerchant, email: e.target.value})}
                  placeholder="contact@merchant.com"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Website</label>
                <Input
                  value={editingMerchant.website || ''}
                  onChange={(e) => setEditingMerchant({...editingMerchant, website: e.target.value})}
                  placeholder="https://merchant.com"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <label className="text-sm font-medium">Description</label>
                <Input
                  value={editingMerchant.description || ''}
                  onChange={(e) => setEditingMerchant({...editingMerchant, description: e.target.value})}
                  placeholder="Brief description of the business"
                />
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Primary Country</label>
                <Select value={editingMerchant.primary_country} onValueChange={(value) => setEditingMerchant({...editingMerchant, primary_country: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="US">🇺🇸 United States</SelectItem>
                    <SelectItem value="GB">🇬🇧 United Kingdom</SelectItem>
                    <SelectItem value="MX">🇲🇽 Mexico</SelectItem>
                    <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                    <SelectItem value="DE">🇩🇪 Germany</SelectItem>
                    <SelectItem value="FR">🇫🇷 France</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Primary Currency</label>
                <Select value={editingMerchant.primary_currency} onValueChange={(value) => setEditingMerchant({...editingMerchant, primary_currency: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="CAD">CAD</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2 flex items-center gap-3 p-3 rounded-lg border border-input bg-muted/40">
                <Checkbox
                  id="edit-uses-virtual-cards"
                  checked={!!editingMerchant.uses_virtual_cards}
                  onCheckedChange={(v) => setEditingMerchant({...editingMerchant, uses_virtual_cards: !!v})}
                  data-testid="edit-merchant-uses-virtual-cards"
                />
                <label htmlFor="edit-uses-virtual-cards" className="text-sm font-medium cursor-pointer select-none">
                  Uses Virtual Cards
                </label>
                <span className="text-xs text-muted-foreground ml-auto">
                  Mark this merchant if it primarily uses virtual / tokenized card products.
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateMerchant} disabled={!editingMerchant?.name}>
              Update Merchant
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

        <CardContent>
          {merchants.length === 0 ? (
            <div className="text-center py-8">
              <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No merchants found</h3>
              <p className="text-muted-foreground">
                {user?.is_admin ? 'Create your first merchant to get started.' : 'No merchants available for your current selection.'}
              </p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Business Type</TableHead>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Total Sales ({filters.currency || 'USD'})</TableHead>
                    <TableHead className="text-right">Avg. Approved Order</TableHead>
                    <TableHead className="text-right">Unique Customers</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Virtual Cards</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {merchants.map((merchant) => (
                    <TableRow key={merchant.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{merchant.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {merchant.email && (
                              <span>{merchant.email}</span>
                            )}
                            {merchant.website && (
                              <span className="ml-2">
                                <Globe className="h-3 w-3 inline mr-1" />
                                {merchant.website.replace('https://', '').replace('http://', '')}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={getMerchantGroupColor(merchant.merchant_group)}>
                          {getMerchantGroupIcon(merchant.merchant_group)} {merchant.merchant_group.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {merchant.platform_name || 'Unknown Platform'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm font-medium">
                          {currencySymbol}{(merchant.total_sales || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {(merchant.transaction_count || 0).toLocaleString()} transactions
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm font-medium">
                          {currencySymbol}{merchant.transaction_count > 0
                            ? ((merchant.total_sales || 0) / merchant.transaction_count).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '0.00'
                          }
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-sm font-medium">
                          {(merchant.unique_customers || 0).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(merchant.status)}>
                          {merchant.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {merchant.uses_virtual_cards ? (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200" data-testid={`merchant-${merchant.id}-virtual-cards-yes`}>
                            <CreditCard className="h-3 w-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid={`merchant-${merchant.id}-virtual-cards-no`}>—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(merchant.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {user?.is_admin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <Edit className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => openEditDialog(merchant)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-red-600" 
                                onClick={() => deleteMerchant(merchant.id, merchant.name)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((pagination.page - 1) * pagination.per_page) + 1} to {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} merchants
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Merchants;